import { useCurrentStudio } from '~entities/studios/model/studio-store';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';

import { PREVIEW_TOKEN_VALUES } from '../types';
import { replaceTokens, sanitizeHtml } from '../utils/sanitizeHtml';

interface EmailPreviewDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: string;
  htmlContent: string;
  logoUrl?: string | null;
  studioName?: string;
  supportEmail?: string;
}

function renderEmailHeader(
  logoUrl?: string | null,
  studioName?: string,
): string {
  if (logoUrl) {
    return `
      <div style="text-align: center; margin-bottom: 32px;">
        <img
          src='${logoUrl}'
          alt='Studio Logo'
          style="max-height: 48px; max-width: 200px; object-fit: contain; margin: 0 auto; display: inline-block;"
          onerror="this.style.display='none'"
        />
      </div>
    `;
  }

  const displayName = studioName ?? 'Your Studio';
  return `
    <div style="text-align: center; margin-bottom: 32px;">
      <span style="font-family: 'Inter', Arial, sans-serif; font-size: 1.125rem; font-weight: 600; letter-spacing: -0.025em; color: #000814;">
        ${displayName}
      </span>
    </div>
  `;
}

function renderEmailBody(htmlContent: string): string {
  return `
    <div class="email-preview-content" style="margin-bottom: 32px;">
      <div style="font-family: 'Inter', Arial, sans-serif; font-size: 15px; line-height: 1.7; color: #111827;">
        ${htmlContent}
      </div>
    </div>
  `;
}

function renderSystemFooter(): string {
  return `
    <hr style="border: 0; border-top: 1px solid #E5E7EB; margin: 32px 0;" />
    <div style="margin-bottom: 32px; text-align: center;">
      <p style="margin-bottom: 16px; font-family: 'Inter', Arial, sans-serif; font-size: 15px; line-height: 1.7; color: #111827;">
        Your temporary password is <strong style="font-weight: 600; color: #000814;">{password}</strong>
      </p>
      <p style="font-family: 'Inter', Arial, sans-serif; font-size: 15px; line-height: 1.7; color: #111827;">
        <a href="{parent_portal_link}" style="color: #3B82F6; text-decoration: underline;">
          Login to the parent portal here
        </a>
      </p>
    </div>
  `;
}

function renderEmailFooter(supportEmail?: string): string {
  const currentYear = new Date().getFullYear();
  const contactEmail = supportEmail ?? 'support@enrollio.ai';
  // Use actual Enrollio logo from public assets
  const enrollioLogo = '/enrollio-logo.svg';

  return `
    <hr style="border: 0; border-top: 1px solid #E5E7EB; margin: 32px 0;" />
    <div style="text-align: center;">
      <img 
        src="${enrollioLogo}" 
        alt="Enrollio" 
        style="max-height: 24px; max-width: 160px; object-fit: contain; margin: 0 auto 12px auto; display: inline-block;" 
      />
      <p style="font-family: 'Inter', Arial, sans-serif; font-size: 13px; line-height: 1.5; color: #6B7280; margin: 0;">
        © ${currentYear} Enrollio. All rights reserved.<br />
        Need help? Contact us at <a href="mailto:${contactEmail}" style="color: #3B82F6; text-decoration: underline;">${contactEmail}</a>
      </p>
    </div>
  `;
}

export function EmailPreviewDrawer({
  open,
  onOpenChange,
  subject,
  htmlContent,
  logoUrl,
  studioName,
  supportEmail,
}: EmailPreviewDrawerProps) {
  const studio = useCurrentStudio();
  const effectiveStudioName =
    studioName ?? studio?.subaccountName ?? 'Your Studio';

  // TODO: Get location.email from GHL API when backend endpoint is available
  // For now, use supportEmail prop or fall back to Enrollio support
  const effectiveSupportEmail = supportEmail;

  // Build the complete email HTML
  const sanitized = sanitizeHtml(htmlContent);
  const fullEmail = `
    <style>
      .email-preview-content ul {
        list-style-type: disc;
        padding-left: 1.5em;
        margin: 0.5em 0;
      }
      .email-preview-content ol {
        list-style-type: decimal;
        padding-left: 1.5em;
        margin: 0.5em 0;
      }
      .email-preview-content ul ul {
        list-style-type: circle;
      }
      .email-preview-content ul ul ul {
        list-style-type: square;
      }
      .email-preview-content ol ol {
        list-style-type: lower-alpha;
      }
      .email-preview-content ol ol ol {
        list-style-type: lower-roman;
      }
      .email-preview-content li {
        margin: 0.25em 0;
      }
      .email-preview-content p {
        margin: 0.75em 0;
      }
    </style>
    ${renderEmailHeader(logoUrl, effectiveStudioName)}
    ${renderEmailBody(sanitized)}
    ${renderSystemFooter()}
    ${renderEmailFooter(effectiveSupportEmail)}
  `;

  // Replace tokens with preview values (including studio name)
  const tokenValues = {
    ...PREVIEW_TOKEN_VALUES,
    '{studio_name}': effectiveStudioName,
  };
  const previewHtml = replaceTokens(fullEmail, tokenValues);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='w-full overflow-y-auto sm:max-w-2xl'
      >
        <SheetHeader className='mb-6'>
          <SheetTitle className='text-2xl'>Email Preview</SheetTitle>
          <SheetDescription>
            See how your email will appear to recipients
          </SheetDescription>
        </SheetHeader>

        {/* Subject line preview */}
        <div className='mb-6 rounded-lg border border-[#E5E7EB] bg-gray-50 p-4'>
          <p className='text-sm font-medium text-gray-500'>Subject:</p>
          <p className='text-lg font-semibold text-gray-900'>
            {replaceTokens(subject, tokenValues)}
          </p>
        </div>

        {/* Email content preview - unified white background */}
        <div className='mx-auto max-w-screen-sm space-y-6 rounded-xl border border-[#E5E7EB] bg-white px-6 py-10 shadow-sm'>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
